import { ArrowPathIcon, ArrowsPointingOutIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useRef, useState } from 'react';
import { Responsive, Layout } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

const ResponsiveGridLayout = Responsive;

interface StockingGuideGridProps {
  children: React.ReactNode[];
  isDraggable: boolean;
  onLayoutChange?: (layout: Layout) => void;
  onResetLayout?: () => void;
}

const defaultLayouts = {
  lg: [
    { i: 'metric-1', x: 0, y: 0, w: 3, h: 2 },
    { i: 'metric-2', x: 3, y: 0, w: 3, h: 2 },
    { i: 'metric-3', x: 6, y: 0, w: 3, h: 2 },
    { i: 'metric-4', x: 9, y: 0, w: 3, h: 2 },
    { i: 'analytics', x: 0, y: 2, w: 6, h: 4 },
    { i: 'actions', x: 6, y: 2, w: 6, h: 4 },
    { i: 'insights', x: 0, y: 6, w: 12, h: 5 },
  ],
  md: [
    { i: 'metric-1', x: 0, y: 0, w: 5, h: 2 },
    { i: 'metric-2', x: 5, y: 0, w: 5, h: 2 },
    { i: 'metric-3', x: 0, y: 2, w: 5, h: 2 },
    { i: 'metric-4', x: 5, y: 2, w: 5, h: 2 },
    { i: 'analytics', x: 0, y: 4, w: 10, h: 4 },
    { i: 'actions', x: 0, y: 8, w: 10, h: 4 },
    { i: 'insights', x: 0, y: 12, w: 10, h: 6 },
  ],
  sm: [
    { i: 'metric-1', x: 0, y: 0, w: 3, h: 2 },
    { i: 'metric-2', x: 3, y: 0, w: 3, h: 2 },
    { i: 'metric-3', x: 0, y: 2, w: 3, h: 2 },
    { i: 'metric-4', x: 3, y: 2, w: 3, h: 2 },
    { i: 'analytics', x: 0, y: 4, w: 6, h: 4 },
    { i: 'actions', x: 0, y: 8, w: 6, h: 4 },
    { i: 'insights', x: 0, y: 12, w: 6, h: 6 },
  ],
};

const getSavedLayout = (key: string) => {
  try {
    const saved = localStorage.getItem(key);
    return saved ? JSON.parse(saved) : null;
  } catch (e) {
    return null;
  }
};

const StockingGuideGrid: React.FC<StockingGuideGridProps> = ({
  children,
  isDraggable,
  onLayoutChange,
  onResetLayout,
}) => {
  const [layouts, setLayouts] = useState(getSavedLayout('stockingGuideLayout') || defaultLayouts);
  const [width, setWidth] = useState(1200);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    // Set initial width
    setWidth(containerRef.current.offsetWidth);

    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        setWidth(entry.contentRect.width);
      }
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // Save layout when it changes
  const handleLayoutChange = (currentLayout: Layout, allLayouts: any) => {
    setLayouts(allLayouts);
    localStorage.setItem('stockingGuideLayout', JSON.stringify(allLayouts));
    if (onLayoutChange) {
      onLayoutChange(currentLayout);
    }
  };

  const resetLayout = () => {
    setLayouts(defaultLayouts);
    localStorage.removeItem('stockingGuideLayout');
    if (onResetLayout) onResetLayout();
  };

  // Map children to their grid items based on key
  // We use direct mapping to avoid React.Children.map key munging which breaks React Grid Layout matching
  const gridChildren = (Array.isArray(children) ? children : [children]).map((child) => {
    if (!React.isValidElement(child)) return null;
    const key = child.key as string;
    return (
      <div
        key={key}
        className={`group bg-transparent transition-all duration-300 scrollbar-hide ${isDraggable ? 'p-1 hover:z-50' : ''}`}
      >
        <div
          className={`h-full w-full overflow-hidden transition-shadow border-transparent scrollbar-hide ${isDraggable ? 'ring-2 ring-blue-400 ring-opacity-50 rounded-xl shadow-md bg-white' : ''}`}
        >
          {isDraggable && (
            <div className="grid-drag-handle absolute top-3 right-3 z-50 opacity-0 group-hover:opacity-100 cursor-grab active:cursor-grabbing bg-blue-600 text-white rounded-lg p-1.5 shadow-lg transition-all hover:scale-110">
              <ArrowsPointingOutIcon className="w-4 h-4" title="Drag to move" />
            </div>
          )}
          <div className="h-full w-full scrollbar-hide">{child}</div>
        </div>
      </div>
    );
  });

  return (
    <div
      className={`relative min-h-[500px] transition-colors duration-500 rounded-3xl scrollbar-hide ${isDraggable ? 'bg-blue-50/30 p-4' : ''}`}
      ref={containerRef}
    >
      {isDraggable && (
        <div className="mb-6 flex justify-between items-center bg-white p-4 rounded-xl border border-blue-200 shadow-sm">
          <div className="flex items-center text-blue-800 font-medium">
            <div className="bg-blue-100 p-2 rounded-lg mr-3">
              <ArrowsPointingOutIcon className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm">Layout Customization Active</p>
              <p className="text-xs text-blue-500 font-normal">
                Drag handles show on hover. Changes save automatically.
              </p>
            </div>
          </div>
          <button
            onClick={resetLayout}
            className="text-sm font-semibold text-red-600 hover:text-red-700 flex items-center px-4 py-2 hover:bg-red-50 rounded-lg border border-transparent hover:border-red-200 transition-all"
          >
            <ArrowPathIcon className="w-4 h-4 mr-2" />
            Reset to Default
          </button>
        </div>
      )}

      <ResponsiveGridLayout
        className="layout"
        layouts={layouts}
        width={width}
        breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
        cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
        rowHeight={85}
        isDraggable={isDraggable}
        isResizable={isDraggable}
        draggableHandle=".grid-drag-handle"
        onLayoutChange={handleLayoutChange}
        margin={[24, 24]}
        containerPadding={[12, 12]}
      >
        {gridChildren}
      </ResponsiveGridLayout>
    </div>
  );
};

export default StockingGuideGrid;
